import { Controller } from "@hotwired/stimulus";

type EmailFrameHeightMessage = {
    type: string;
    height: number;
};

const EMAIL_FRAME_HEIGHT_MESSAGE_TYPE = "elron-email-frame-height";
const DEFAULT_MIN_HEIGHT = 96;
const DEFAULT_MAX_HEIGHT = 2400;

export default class extends Controller<HTMLElement> {
    static targets = ["frame"];
    static values = {
        maxHeight: Number,
        minHeight: Number,
    };

    declare readonly frameTarget: HTMLIFrameElement;
    declare readonly hasFrameTarget: boolean;
    declare readonly hasMaxHeightValue: boolean;
    declare readonly hasMinHeightValue: boolean;
    declare readonly maxHeightValue: number;
    declare readonly minHeightValue: number;

    private readonly handleMessage = (event: MessageEvent<unknown>): void => {
        if (!this.hasFrameTarget || event.source !== this.frameTarget.contentWindow) {
            return;
        }

        if (!this.isHeightMessage(event.data)) {
            return;
        }

        const height = Math.min(
            this.maximumHeight(),
            Math.max(this.minimumHeight(), Math.ceil(event.data.height))
        );

        const scrollPanel = this.element.closest<HTMLElement>("#tickets-detail-action-scroll");
        const scrollTop = scrollPanel?.scrollTop;
        this.frameTarget.style.height = `${height}px`;
        if (scrollPanel && scrollTop !== undefined) scrollPanel.scrollTop = scrollTop;
        this.frameTarget.dataset.ticketEmailFrameHeight = String(height);
        this.element.dataset.ticketEmailFrameReady = "true";
    };

    connect(): void {
        window.addEventListener("message", this.handleMessage);
    }

    disconnect(): void {
        window.removeEventListener("message", this.handleMessage);
    }

    private isHeightMessage(value: unknown): value is EmailFrameHeightMessage {
        if (typeof value !== "object" || value === null) {
            return false;
        }

        const candidate = value as Partial<EmailFrameHeightMessage>;
        return (
            candidate.type === EMAIL_FRAME_HEIGHT_MESSAGE_TYPE
            && typeof candidate.height === "number"
            && Number.isFinite(candidate.height)
            && candidate.height > 0
        );
    }

    private minimumHeight(): number {
        return this.hasMinHeightValue && this.minHeightValue > 0
            ? this.minHeightValue
            : DEFAULT_MIN_HEIGHT;
    }

    private maximumHeight(): number {
        const minimumHeight = this.minimumHeight();
        const configuredMaximum =
            this.hasMaxHeightValue && this.maxHeightValue > 0
                ? this.maxHeightValue
                : DEFAULT_MAX_HEIGHT;

        return Math.max(minimumHeight, configuredMaximum);
    }
}
